import {
  calculateDistributionLines,
  DEFAULT_COMPONENTS,
  postDistributionEntries,
} from '../../domain/distributionCalc';
import { addDays, DEMO_TODAY } from '../../lib/format';
import type { Random } from '../../lib/random';
import type {
  CallLine,
  CapitalReturn,
  RedemptionLine,
  CapitalCall,
  Commitment,
  Distribution,
  Holder,
  LedgerEntry,
  TaxComponents,
} from '../types';
import { seedLifecycleLedger } from './seedLifecycle';
import { CLASSES, FIDES_TENANT_ID, MIGRATION_DATE, priceAt } from './seedReference';

const AMOUNTS = [
  50_000, 100_000, 100_000, 150_000, 200_000, 200_000, 250_000, 300_000, 400_000, 500_000, 750_000,
  1_000_000, 1_500_000,
];

export interface LedgerSeed {
  ledger: LedgerEntry[];
  commitments: Commitment[];
  calls: CapitalCall[];
  distributions: Distribution[];
  capitalReturns: CapitalReturn[];
  pastRedemptions: RedemptionLine[];
}

export type Draft = Omit<LedgerEntry, 'seq'>;

export const classById = (id: string) => CLASSES.find((unitClass) => unitClass.id === id)!;

export function entry(
  holderId: string,
  classId: string,
  type: LedgerEntry['type'],
  effectiveDate: string,
  amount: number,
  memo: string,
  reference: string,
): Draft {
  const unitClass = classById(classId);
  const price = priceAt(unitClass, effectiveDate);
  return {
    id: `le-${holderId}-${classId}-${type}-${effectiveDate}`,
    fundId: unitClass.fundId,
    classId,
    holderId,
    type,
    effectiveDate,
    postedAt: `${effectiveDate}T10:00:00`,
    units: Math.round((amount / price) * 10_000) / 10_000,
    amount,
    unitPrice: price,
    reference,
    memo,
    postedBy: type === 'MIGRATION_OPENING' ? 'Migration' : 'Alex Morgan',
    approvedBy: type === 'MIGRATION_OPENING' ? 'Priya Nair' : 'Priya Nair',
  };
}

export const opening = (
  holderId: string,
  classId: string,
  amount: number,
  source = 'Registry Direct',
) =>
  entry(
    holderId,
    classId,
    'MIGRATION_OPENING',
    MIGRATION_DATE,
    amount,
    `Opening balance migrated from ${source}`,
    'MIGRATION',
  );

const issue = (holderId: string, classId: string, date: string, amount: number) =>
  entry(holderId, classId, 'ISSUE', date, amount, 'Units issued on application', `APP ${date}`);

function oppCommitment(
  holderId: string,
  amount: number,
  drafts: Draft[],
  commitments: Commitment[],
) {
  commitments.push({
    id: `cm-${holderId}`,
    holderId,
    fundId: 'f-opp',
    classId: 'c-opp-o',
    amount,
    date: '2025-02-15',
  });
  drafts.push(opening(holderId, 'c-opp-o', Math.round(amount * 0.7)));
}

function personaAllocations(drafts: Draft[], commitments: Commitment[]) {
  drafts.push(opening('h-bennett-ind', 'c-psd-a', 250_000));
  drafts.push(opening('h-bennett-ind', 'c-core-a', 150_000));
  oppCommitment('h-bennett-smsf', 500_000, drafts, commitments);
  drafts.push(opening('h-bennett-smsf', 'c-mez-o', 300_000));
  drafts.push(issue('h-bennett-trust', 'c-psd-a', '2026-08-01', 400_000));
  drafts.push(opening('h-mehta-co', 'c-psd-w', 1_200_000));
  oppCommitment('h-mehta-co', 1_000_000, drafts, commitments);
}

function allocate(random: Random, holders: Holder[], drafts: Draft[], commitments: Commitment[]) {
  for (const holder of holders) {
    if (holder.investorLoginId === 'il-bennett' || holder.id === 'h-mehta-co') continue;
    if (holder.tenantId !== FIDES_TENANT_ID) {
      drafts.push(opening(holder.id, 'c-hdcf-a', random.pick(AMOUNTS), 'Automic'));
      continue;
    }
    const amount = () => random.pick(AMOUNTS);
    if (random.chance(0.62)) {
      const value = amount();
      const classId = value >= 1_000_000 ? 'c-psd-w' : 'c-psd-a';
      drafts.push(
        random.chance(0.85)
          ? opening(holder.id, classId, value)
          : issue(holder.id, classId, addDays('2026-07-01', random.int(0, 3) * 31), value),
      );
    }
    if (random.chance(0.35))
      oppCommitment(holder.id, Math.max(250_000, amount()), drafts, commitments);
    if (random.chance(0.3)) drafts.push(opening(holder.id, 'c-mez-o', amount()));
    if (random.chance(0.42)) {
      drafts.push(
        random.chance(0.8)
          ? opening(holder.id, 'c-core-a', amount())
          : issue(holder.id, 'c-core-a', '2026-09-01', amount()),
      );
    }
  }
  const firstClose = holders.filter((holder) => holder.tenantId === FIDES_TENANT_ID).slice(10, 18);
  for (const holder of firstClose)
    drafts.push(issue(holder.id, 'c-sdf2-a', '2026-10-01', random.pick(AMOUNTS)));
}

function buildCall(random: Random, drafts: Draft[], commitments: Commitment[]): CapitalCall {
  const lines: CallLine[] = commitments.map((commitment, index) => {
    const amountDue = Math.round(commitment.amount * 0.2);
    const roll = commitment.holderId === 'h-bennett-smsf' ? 1 : random.next();
    const paid = roll < 0.55 ? amountDue : roll < 0.65 ? Math.round(amountDue / 2) : 0;
    if (paid > 0) {
      const paidOn = addDays('2026-09-25', random.int(0, 17));
      drafts.push({
        ...entry(
          commitment.holderId,
          'c-opp-o',
          'CALL_PAYMENT',
          paidOn,
          paid,
          'Call 3 payment received',
          'CALL 3',
        ),
        id: `le-call3-${commitment.holderId}`,
        eventId: 'cc-opp-3',
      });
    }
    return {
      holderId: commitment.holderId,
      commitment: commitment.amount,
      amount: amountDue,
      paid,
      status: paid === amountDue ? 'PAID' : paid > 0 ? 'PARTIAL' : 'UNPAID',
      crn: `7700${String(1000 + index * 7)}`,
    };
  });
  return {
    id: 'cc-opp-3',
    fundId: 'f-opp',
    classId: 'c-opp-o',
    number: 3,
    callPct: 20,
    purpose: 'Equity for the Northern Growth Corridor JV (Stage 2 civil works)',
    issueDate: '2026-09-22',
    dueDate: '2026-10-13',
    status: 'ISSUED',
    createdBy: 'Alex Morgan',
    approvedBy: 'Priya Nair',
    lines,
  };
}

const historicCalls = (): CapitalCall[] => [
  {
    id: 'cc-opp-1',
    fundId: 'f-opp',
    classId: 'c-opp-o',
    number: 1,
    callPct: 40,
    purpose: 'Initial deployment (pre-migration, history imported)',
    issueDate: '2025-03-01',
    dueDate: '2025-03-21',
    status: 'CLOSED',
    createdBy: 'Migration',
    lines: [],
  },
  {
    id: 'cc-opp-2',
    fundId: 'f-opp',
    classId: 'c-opp-o',
    number: 2,
    callPct: 30,
    purpose: 'Second deployment (pre-migration, history imported)',
    issueDate: '2025-11-03',
    dueDate: '2025-11-24',
    status: 'CLOSED',
    createdBy: 'Migration',
    lines: [],
  },
];

interface DistributionSpec {
  id: string;
  classId: string;
  periodStart: string;
  periodEnd: string;
  paymentDate: string;
  centsPerUnit: number;
  status: Distribution['status'];
  components?: TaxComponents;
  drp?: boolean;
}

const DISTRIBUTION_SPECS: DistributionSpec[] = [
  ...['07', '08', '09'].flatMap((month, index) => [
    {
      id: `ds-psd-a-${month}`,
      classId: 'c-psd-a',
      periodStart: `2026-${month}-01`,
      periodEnd: ['2026-07-31', '2026-08-31', '2026-09-30'][index],
      paymentDate: `2026-${String(Number(month) + 1).padStart(2, '0')}-10`,
      centsPerUnit: 0.7083,
      status: 'PAID' as const,
    },
    {
      id: `ds-psd-w-${month}`,
      classId: 'c-psd-w',
      periodStart: `2026-${month}-01`,
      periodEnd: ['2026-07-31', '2026-08-31', '2026-09-30'][index],
      paymentDate: `2026-${String(Number(month) + 1).padStart(2, '0')}-10`,
      centsPerUnit: 0.75,
      status: 'PAID' as const,
    },
    {
      id: `ds-hdcf-${month}`,
      classId: 'c-hdcf-a',
      periodStart: `2026-${month}-01`,
      periodEnd: ['2026-07-31', '2026-08-31', '2026-09-30'][index],
      paymentDate: `2026-${String(Number(month) + 1).padStart(2, '0')}-12`,
      centsPerUnit: 0.6667,
      status: 'PAID' as const,
    },
  ]),
  {
    id: 'ds-mez-q1',
    classId: 'c-mez-o',
    periodStart: '2026-07-01',
    periodEnd: '2026-09-30',
    paymentDate: '2026-10-12',
    centsPerUnit: 3,
    status: 'PAID',
    components: {
      interest: 1,
      otherIncome: 0,
      discountedGain: 0,
      taxDeferred: 0,
      foreignIncome: 0,
    },
  },
  {
    id: 'ds-opp-q1',
    classId: 'c-opp-o',
    periodStart: '2026-07-01',
    periodEnd: '2026-09-30',
    paymentDate: '2026-10-28',
    centsPerUnit: 2.5,
    status: 'PENDING_APPROVAL',
    components: {
      interest: 0.35,
      otherIncome: 0.4,
      discountedGain: 0.2,
      taxDeferred: 0.05,
      foreignIncome: 0,
    },
  },
  {
    id: 'ds-core-q1',
    classId: 'c-core-a',
    periodStart: '2026-07-01',
    periodEnd: '2026-09-30',
    paymentDate: '2026-10-30',
    centsPerUnit: 1.75,
    status: 'DRAFT',
    drp: true,
    components: {
      interest: 0.1,
      otherIncome: 0.55,
      discountedGain: 0.05,
      taxDeferred: 0.3,
      foreignIncome: 0,
    },
  },
];

function buildDistributions(ledgerDrafts: Draft[], holders: Holder[]): Distribution[] {
  const distributions: Distribution[] = [];
  const working = ledgerDrafts as LedgerEntry[];
  for (const spec of [...DISTRIBUTION_SPECS].sort((a, b) =>
    a.paymentDate.localeCompare(b.paymentDate),
  )) {
    const unitClass = classById(spec.classId);
    const components = spec.components ?? DEFAULT_COMPONENTS;
    const lines = calculateDistributionLines(
      {
        classId: spec.classId,
        recordDate: spec.periodEnd,
        centsPerUnit: spec.centsPerUnit,
        components,
        drpEnabled: Boolean(spec.drp),
      },
      working,
      holders,
    );
    const distribution: Distribution = {
      id: spec.id,
      fundId: unitClass.fundId,
      classId: spec.classId,
      periodStart: spec.periodStart,
      periodEnd: spec.periodEnd,
      recordDate: spec.periodEnd,
      paymentDate: spec.paymentDate,
      centsPerUnit: spec.centsPerUnit,
      status: spec.status,
      components,
      createdBy: 'Alex Morgan',
      approvedBy: spec.status === 'PAID' ? 'Priya Nair' : undefined,
      abaFileName:
        spec.status === 'PAID'
          ? `${unitClass.fundId.toUpperCase()}_${spec.periodEnd}.aba`
          : undefined,
      lines,
    };
    distributions.push(distribution);
    if (spec.status === 'PAID' && spec.paymentDate <= DEMO_TODAY) {
      working.push(...postDistributionEntries(distribution, unitClass, 0, 'Alex Morgan'));
    }
  }
  return distributions;
}

export function generateLedger(random: Random, holders: Holder[]): LedgerSeed {
  const drafts: Draft[] = [];
  const commitments: Commitment[] = [];
  personaAllocations(drafts, commitments);
  allocate(random, holders, drafts, commitments);
  const call = buildCall(random, drafts, commitments);
  const lifecycle = seedLifecycleLedger(random, holders, drafts);
  const distributions = buildDistributions(drafts, holders);
  const ledger = (drafts as LedgerEntry[])
    .sort((left, right) => left.postedAt.localeCompare(right.postedAt))
    .map((item, index) => ({ ...item, seq: index + 1 }));
  return { ledger, commitments, calls: [...historicCalls(), call], distributions, ...lifecycle };
}
